"use client";
import classNames from "classnames/bind";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import type { ModalProps } from "./Modal.types.ts";

import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { CloseOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/CloseOutlined";
import { useClearTimeoutRef } from "./utils/useClearTimeoutRef.ts";
import { Portal } from "../Portal/Portal";

import styles from "./Modal.module.scss";

const cx = classNames.bind(styles);

export const Modal = ({
  children,
  isOpen = false,
  className,
  keepMounted = false,
  disablePortal = false,
  portalContainer,
  enableBodyScrollOverride = true,
  onClose = () => {},
}: ModalProps) => {
  const [dialogNode, setDialogNode] = useState<HTMLDialogElement | null>(null);
  const modalDialogRef = React.useRef<HTMLDialogElement | null>(null);
  const [modalClosing, setModalClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMounted, setIsMounted] = useState(isOpen || keepMounted);
  const [isDesktop, setIsDesktop] = useState(false);

  const headingId = useId();

  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event?.key === "Escape") {
      closeModal();
    }
  }, []);

  const handleOverlayClick = useCallback((event: MouseEvent) => {
    if (event.target === modalDialogRef?.current) {
      closeModal();
    }
  }, []);

  const handleCloseButtonClick = useCallback(() => {
    closeModal();
  }, []);

  const setBodyScroll = (open: boolean) => {
    if (enableBodyScrollOverride) {
      document.body.style.overflow = open ? "hidden" : "";
    }
  };

  const clearCloseTimeout = useClearTimeoutRef(closeTimeoutRef);
  const clearUnmountTimeout = useClearTimeoutRef(unmountTimeoutRef);

  const clearPendingTimeouts = useCallback(() => {
    clearCloseTimeout();
    clearUnmountTimeout();
  }, [clearCloseTimeout, clearUnmountTimeout]);

  const cleanupEventListeners = () => {
    document?.removeEventListener("keydown", handleEscape);
    modalDialogRef?.current?.removeEventListener("click", handleOverlayClick);
    modalDialogRef?.current?.removeEventListener(
      "close",
      handleDialogCloseEvent,
    );
  };

  const closeModal = () => {
    if (!modalDialogRef?.current?.open) {
      return;
    }
    clearCloseTimeout();
    setModalClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      cleanupEventListeners();
      modalDialogRef?.current?.close();
      setBodyScroll(false);
      setModalClosing(false);
      closeTimeoutRef.current = null;
    }, 500);
  };

  const handleDialogCloseEvent = () => {
    cleanupEventListeners();
    setBodyScroll(false);
    onClose();
  };

  const openModal = () => {
    if (!dialogNode) {
      return;
    }

    if (!dialogNode.open) {
      document.addEventListener("keydown", handleEscape);
      dialogNode.showModal();
      dialogNode.focus();
      dialogNode.addEventListener("click", handleOverlayClick);
      dialogNode.addEventListener("close", handleDialogCloseEvent);
      setBodyScroll(true);
    }
  };

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)");

    const handleResize = () => {
      setIsDesktop(isDesktop.matches);
    };
    handleResize();

    isDesktop.addEventListener("change", handleResize);

    return () => {
      window.removeEventListener("change", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleDialog = async () => {
      if (isOpen) {
        clearPendingTimeouts();
        setModalClosing(false);
      }
    };

    handleDialog();

    return () => {
      cleanupEventListeners();
      setBodyScroll(false);
    };
  }, [isOpen, handleEscape, handleOverlayClick]);

  useEffect(() => {
    if (isOpen && dialogNode) {
      openModal();
    }

    if (!isOpen && dialogNode && dialogNode.open) {
      closeModal();
    }
  }, [isOpen, dialogNode]);

  useEffect(() => {
    if (keepMounted) {
      clearUnmountTimeout();
      setIsMounted(true);
      return clearPendingTimeouts;
    }

    if (isOpen) {
      clearUnmountTimeout();
      setIsMounted(true);
    } else if (isMounted) {
      clearUnmountTimeout();
      unmountTimeoutRef.current = setTimeout(() => {
        setIsMounted(false);
        unmountTimeoutRef.current = null;
      }, 500);
    }

    return () => {
      clearPendingTimeouts();
    };
  }, [
    isOpen,
    isMounted,
    keepMounted,
    clearPendingTimeouts,
    clearUnmountTimeout,
  ]);

  if (!isMounted) {
    return null;
  }

  const modal = (
    <dialog
      ref={(node) => {
        modalDialogRef.current = node;
        setDialogNode(node);
      }}
      aria-labelledby={headingId}
      className={cx(styles.modal, { modalClosing }, className)}
      data-jlds={true}
      data-testid="modal"
    >
      <div className={styles.modal__content}>
        <div
          className={styles.modal__body}
          data-testid="modal-body"
          tabIndex={0}
        >
          {children}
        </div>
        <IconButton
          aria-label="Modal Close Button"
          className={styles.closeButton}
          data-testid="modal-close-button"
          icon={CloseOutlined}
          background="filled"
          size={isDesktop ? "medium" : "small"}
          onClick={handleCloseButtonClick}
        />
      </div>
    </dialog>
  );

  if (disablePortal) {
    return modal;
  }

  return <Portal selector={portalContainer}>{modal}</Portal>;
};
